import { useEffect, useState } from "react";
import {
  cardBase,
  cardSubtle,
  cardSurface,
  inputGlowWrap,
  inputGlowInner,
} from "@/lib/card-styles";
import {
  getMeasurementStats,
  listBuildingServices,
  listEquipment,
  listZones,
  type BuildingService,
  type BuildingServiceType,
  type BuildingTarget,
  type MeasurementStats,
} from "@/lib/api/building";
import {
  getLatestPotential,
  runPotentialSimulation,
  type OptimizationPotentialBreakdown,
  type OptimizationPotentialReport,
  type UnallocatedSavings,
  type ZoneSavingsAllocation,
} from "@/lib/api/simulation";
import {
  SIMULATION_SCENARIOS,
  resolvedEquipmentZoneIds,
  type EquipmentCard,
  type ScenarioDefinition,
} from "@/lib/building-domain";
import {
  equipmentTypeNameFi,
  useEquipmentTypeMeta,
  useKbSensorTypes,
} from "@/lib/equipment-type-meta";
import { cn } from "@/lib/utils";
import { applyParamChange } from "./simulation-params";

// ── Types ─────────────────────────────────────────────────────────────────────

type PeriodKey = "30" | "90" | "365";

const PERIOD_LABEL: Record<PeriodKey, string> = {
  "30": "30 vrk",
  "90": "90 vrk",
  "365": "12 kk",
};

const SCENARIO_COLORS: Record<string, string> = {
  co2_demand_ventilation: "bg-sky-500",
  night_setback: "bg-warning",
  electricity_price_optimization: "bg-primary",
  building_usage_setback: "bg-success",
  weather_preconditioning: "bg-blue-400",
  demand_response: "bg-orange-400",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function stats_have_data(
  stats: MeasurementStats,
  scenario: ScenarioDefinition,
): boolean {
  return scenario.required_channels.every((ch) => stats[ch] !== null);
}

// Scenarios that require an external service to reach their full potential
const SCENARIO_REQUIRED_SERVICE: Record<string, BuildingServiceType> = {
  electricity_price_optimization: "electricity-price-optimization",
  weather_preconditioning: "weather-preconditioning",
};

// ── SavingsBreakdownChart ─────────────────────────────────────────────────────

function SavingsBreakdownChart({
  breakdown,
}: {
  breakdown: OptimizationPotentialBreakdown[];
}) {
  const sorted = [...breakdown]
    .filter((b) => b.eur_month > 0)
    .sort((a, b) => b.eur_month - a.eur_month);
  const max_eur = sorted[0]?.eur_month ?? 1;

  return (
    <div className="flex flex-col gap-2.5">
      {sorted.map((b) => {
        const pct = (b.eur_month / max_eur) * 100;
        const confidence_pct = Math.round(b.confidence * 100);
        const bar_color = SCENARIO_COLORS[b.scenario_id] ?? "bg-primary";
        return (
          <div key={b.scenario_id}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs text-muted-foreground truncate">
                {b.scenario_name_fi}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-muted-foreground/60">
                  {confidence_pct}% tietopohja
                </span>
                <span className="text-xs font-semibold text-foreground">
                  €
                  {b.eur_month.toLocaleString("fi-FI", {
                    maximumFractionDigits: 0,
                  })}
                  /kk
                </span>
              </div>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-700",
                  bar_color,
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── SimulationView ────────────────────────────────────────────────────────────

interface Props {
  buildingId: string;
  strategy: BuildingTarget | null;
}

export function SimulationView({ buildingId, strategy }: Props) {
  const [loading, set_loading] = useState(true);
  const [stats, set_stats] = useState<MeasurementStats | null>(null);
  const [equipment, set_equipment] = useState<EquipmentCard[]>([]);
  const [full_report, set_full_report] =
    useState<OptimizationPotentialReport | null>(null);
  const [services, set_services] = useState<BuildingService[]>([]);
  const [zone_names, set_zone_names] = useState<Record<string, string>>({});
  const [period, set_period] = useState<PeriodKey>("90");
  const [detail_scenario, set_detail_scenario] =
    useState<ScenarioDefinition | null>(null);
  const [running_full, set_running_full] = useState(false);

  useEffect(() => {
    async function load() {
      const [
        raw_stats,
        raw_equipment,
        existing_report,
        raw_services,
        raw_zones,
      ] = await Promise.all([
        getMeasurementStats(buildingId).catch(() => null),
        listEquipment(buildingId).catch(() => []),
        getLatestPotential(buildingId),
        listBuildingServices(buildingId).catch(() => [] as BuildingService[]),
        listZones(buildingId).catch(() => []),
      ]);
      set_services(raw_services);
      set_stats(raw_stats);
      set_zone_names(Object.fromEntries(raw_zones.map((z) => [z.id, z.name])));
      set_equipment(
        raw_equipment.map((e) => ({
          id: e.id,
          zone_id: e.zone_id,
          zone_ids: resolvedEquipmentZoneIds(e),
          location: e.location,
          name: e.name,
          equipment_type: e.equipment_type,
          equipment_tag: e.equipment_tag,
          icon: "⚙️",
          color: "#5b8dee",
          optimization_pct:
            typeof e.state["optimization_pct"] === "number"
              ? (e.state["optimization_pct"] as number)
              : 0,
          optimization_color: "#4caf82",
          optimization_enabled: e.optimization_enabled,
          kpis: [],
          data_points: [],
          live_data_points: [],
          state: e.state,
          declared_variants: e.declared_variants ?? [],
          declared_data_points: e.declared_data_points ?? [],
          declared_component_order: e.declared_component_order ?? [],
          declared_component_subtypes: e.declared_component_subtypes ?? {},
          declared_stream_component_order:
            e.declared_stream_component_order ?? {},
        })),
      );
      set_full_report(existing_report);
      set_loading(false);
    }
    void load();
  }, [buildingId]);

  const period_days = parseInt(period);

  const default_params: Record<
    string,
    Record<string, number>
  > = Object.fromEntries(
    SIMULATION_SCENARIOS.map((sc) => {
      const p = { ...sc.default_params };
      if (sc.id === "co2_demand_ventilation" && strategy?.co2_limit_ppm) {
        p["co2_threshold_ppm"] = strategy.co2_limit_ppm;
      }
      return [sc.id, p];
    }),
  );

  const visible_rows = SIMULATION_SCENARIOS.map((sc) => {
    const supporting = equipment.filter((eq) =>
      sc.supported_equipment_types.includes(eq.equipment_type),
    );
    const from_report = full_report?.breakdown.find(
      (b) => b.scenario_id === sc.id,
    );
    const data_ok = stats ? stats_have_data(stats, sc) : false;
    const required_service = SCENARIO_REQUIRED_SERVICE[sc.id] as
      BuildingServiceType | undefined;
    const svc = required_service
      ? services.find((s) => s.service_type === required_service)
      : undefined;
    // Hide only if the service is completely unknown (no record at all); show with badge if inactive
    const service_missing = required_service !== undefined && svc === undefined;
    const service_inactive = svc !== undefined && !svc.is_active;
    return {
      scenario: sc,
      equipment: supporting,
      data_available: data_ok,
      breakdown: from_report ?? null,
      service_missing,
      service_inactive,
      required_service,
    };
  }).filter((r) => (r.equipment.length > 0 || !stats) && !r.service_missing);

  const total_eur = full_report?.total_eur_month ?? 0;
  const total_kwh = full_report?.total_kwh_savings ?? 0;
  const has_report = full_report !== null;
  const has_any_data = visible_rows.some((r) => r.data_available);

  async function handle_run_full() {
    set_running_full(true);
    try {
      const report = await runPotentialSimulation(buildingId, {
        type: "full",
        period_days,
      });
      set_full_report(report);
    } catch {
      // ignore
    } finally {
      set_running_full(false);
    }
  }

  if (loading) {
    return (
      <p className="mt-8 text-center text-sm text-muted-foreground">
        Ladataan...
      </p>
    );
  }

  if (detail_scenario) {
    return (
      <CapabilityDetailPanel
        buildingId={buildingId}
        scenario={detail_scenario}
        equipment={
          visible_rows.find((r) => r.scenario.id === detail_scenario.id)
            ?.equipment ?? []
        }
        full_report={full_report}
        period_days={period_days}
        zone_names={zone_names}
        initial_params={
          default_params[detail_scenario.id] ?? detail_scenario.default_params
        }
        on_back={(updated_report) => {
          if (updated_report) set_full_report(updated_report);
          set_detail_scenario(null);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Period selector */}
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <p className="text-xs text-muted-foreground">
          Laske mitä olisi tapahtunut, jos optimointikyvyt olisivat olleet
          aktiivisina.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            {(Object.keys(PERIOD_LABEL) as PeriodKey[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => set_period(p)}
                className={cn(
                  "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                  period === p
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {PERIOD_LABEL[p]}
              </button>
            ))}
          </div>
          {has_any_data && (
            <button
              type="button"
              onClick={handle_run_full}
              disabled={running_full}
              className="inline-flex items-center gap-1.5 rounded-lg bg-success-subtle text-success-subtle-foreground px-3 py-1.5 text-xs font-medium hover:bg-success/20 disabled:opacity-50 transition-colors"
            >
              {running_full ? (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-success-subtle-foreground/30 border-t-success-subtle-foreground" />
              ) : (
                "⚡"
              )}
              {running_full
                ? "Lasketaan…"
                : has_report
                  ? "Päivitä potentiaali"
                  : "Laske kattopotentiaali"}
            </button>
          )}
        </div>
      </div>

      {/* Potential summary with breakdown chart */}
      {has_report && total_eur > 0 && (
        <div className="rounded-xl border border-success/20 bg-success-subtle/40 p-5">
          <p className="text-xs text-success-subtle-foreground/70 mb-3">
            Kattopotentiaali — jos kaikki optimointikyvyt olisivat aktiivisina
          </p>

          <div className="grid gap-5 lg:grid-cols-2">
            {/* Numbers */}
            <div className="flex flex-col justify-between gap-4">
              <div>
                <p className="text-3xl font-bold text-success-subtle-foreground">
                  €
                  {total_eur.toLocaleString("fi-FI", {
                    maximumFractionDigits: 0,
                  })}
                  /kk
                </p>
                <p className="text-sm font-semibold text-success-subtle-foreground/80 mt-1">
                  {total_kwh.toLocaleString("fi-FI", {
                    maximumFractionDigits: 0,
                  })}{" "}
                  kWh{" "}
                  <span className="text-xs font-normal text-success-subtle-foreground/60">
                    energiansäästö / {PERIOD_LABEL[period]}
                  </span>
                </p>
              </div>
              {full_report.savings_narrative_fi && (
                <div className={cn(cardSubtle, "p-2.5")}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Agentin tulkinta
                  </p>
                  <p className="text-xs text-foreground/80 leading-relaxed">
                    {full_report.savings_narrative_fi}
                  </p>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                ⓘ Laskettu{" "}
                {new Date(full_report.calculated_at).toLocaleDateString(
                  "fi-FI",
                )}
              </p>
            </div>

            {/* Breakdown chart */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Säästöpotentiaali optimointikyvyittäin
              </p>
              <SavingsBreakdownChart breakdown={full_report.breakdown} />
            </div>
          </div>
        </div>
      )}

      {/* No data state */}
      {!has_any_data && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <p className="text-sm font-medium text-muted-foreground mb-1">
            Ei mittausdataa
          </p>
          <p className="text-xs text-muted-foreground">
            Tuo mittausdata Kojelauta-välilehdellä nähdäksesi simuloitu
            säästöpotentiaali.
          </p>
        </div>
      )}

      {/* Optimization capability selection table */}
      {visible_rows.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Optimointikyvyt
          </h3>
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5 text-left">Optimointikyky</th>
                  <th className="px-4 py-2.5 text-left">Talotekniikka</th>
                  <th className="px-4 py-2.5 text-right">Tietopohja</th>
                  <th className="px-4 py-2.5 text-right">Säästö (est.)</th>
                  <th className="px-4 py-2.5 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {visible_rows.map((row) => (
                  <tr
                    key={row.scenario.id}
                    className="hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "shrink-0 w-2 h-2 rounded-full",
                            SCENARIO_COLORS[row.scenario.id] ?? "bg-primary",
                          )}
                        />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">
                              {row.scenario.name_fi}
                            </p>
                            {row.service_inactive && (
                              <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-warning-subtle text-warning-subtle-foreground border border-warning/20">
                                Aktivoi palvelu
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 max-w-xs line-clamp-1">
                            {row.scenario.description_fi}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {row.equipment.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {row.equipment.length} kpl
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.breakdown != null ? (
                        <div className="inline-flex flex-col items-end gap-0.5">
                          <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-cyan-500/70 transition-all duration-500"
                              style={{
                                width: `${Math.round(row.breakdown.confidence * 100)}%`,
                              }}
                            />
                          </div>
                          <span className="text-[9px] text-muted-foreground tabular-nums">
                            {Math.round(row.breakdown.confidence * 100)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.breakdown && row.breakdown.eur_month > 0 ? (
                        <span className="font-semibold text-success-subtle-foreground">
                          €
                          {row.breakdown.eur_month.toLocaleString("fi-FI", {
                            maximumFractionDigits: 0,
                          })}
                          /kk
                        </span>
                      ) : !row.data_available ? (
                        <span className="text-xs text-muted-foreground">
                          Ei dataa
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        disabled={
                          !row.data_available || row.equipment.length === 0
                        }
                        onClick={() => set_detail_scenario(row.scenario)}
                        className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Kokeile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CapabilityDetailPanel ─────────────────────────────────────────────────────

interface DetailProps {
  buildingId: string;
  scenario: ScenarioDefinition;
  equipment: EquipmentCard[];
  full_report: OptimizationPotentialReport | null;
  period_days: number;
  zone_names: Record<string, string>;
  initial_params: Record<string, number>;
  on_back: (updated_report: OptimizationPotentialReport | null) => void;
}

function CapabilityDetailPanel({
  buildingId,
  scenario,
  equipment,
  full_report,
  period_days,
  zone_names,
  initial_params,
  on_back,
}: DetailProps) {
  const eqMeta = useEquipmentTypeMeta();
  const sensors = useKbSensorTypes();
  const [selected_ids, set_selected_ids] = useState<Set<string>>(
    new Set(equipment.map((e) => e.id)),
  );
  const [params, set_params] = useState<Record<string, number>>(initial_params);
  const [result, set_result] = useState<OptimizationPotentialReport | null>(
    null,
  );
  const [running, set_running] = useState(false);

  const ceiling_eur = full_report?.total_eur_month ?? null;
  const this_scenario_ceiling =
    full_report?.breakdown.find((b) => b.scenario_id === scenario.id)
      ?.eur_month ?? null;

  async function run() {
    if (selected_ids.size === 0) return;
    set_running(true);
    set_result(null);
    try {
      const report = await runPotentialSimulation(buildingId, {
        type: "scenario",
        scenario_id: scenario.id,
        params,
        period_days,
      });
      set_result(report);
    } catch {
      // ignore
    } finally {
      set_running(false);
    }
  }

  const scenario_result = result?.breakdown[0] ?? null;

  return (
    <div className="flex flex-col gap-5">
      {/* Back nav */}
      <button
        type="button"
        onClick={() => on_back(null)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors self-start"
      >
        ← Takaisin
      </button>

      <div>
        <h3 className="text-sm font-semibold">{scenario.name_fi}</h3>
        <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
          {scenario.description_fi}
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Equipment selection */}
        <div className={cn(cardBase, "p-4")}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Sisällytä simulaatioon
          </p>
          {equipment.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Ei sopivia laitteita tälle rakennukselle.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {equipment.map((eq) => (
                <label
                  key={eq.id}
                  className="flex items-center gap-2.5 cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    checked={selected_ids.has(eq.id)}
                    onChange={(e) => {
                      set_selected_ids((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(eq.id);
                        else next.delete(eq.id);
                        return next;
                      });
                      set_result(null);
                    }}
                    className="rounded"
                  />
                  <span className="text-sm group-hover:text-foreground transition-colors">
                    {eq.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {equipmentTypeNameFi(eqMeta, eq.equipment_type, sensors)}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Parameters */}
        <div className={cn(cardBase, "p-4")}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Parametrit
          </p>
          <div className="flex flex-col gap-3">
            {Object.entries(scenario.param_labels).map(([key, meta]) => (
              <div
                key={key}
                className="flex items-center justify-between gap-3"
              >
                <label className="text-xs text-muted-foreground flex-1">
                  {meta.label}
                </label>
                <div className="flex items-center gap-1.5">
                  <div className={inputGlowWrap}>
                    <div className={inputGlowInner}>
                      <input
                        type="number"
                        value={params[key] ?? scenario.default_params[key]}
                        onChange={(e) => {
                          set_params((p) =>
                            applyParamChange(p, key, e.target.value),
                          );
                          set_result(null);
                        }}
                        className="w-20 bg-transparent px-2 py-1 text-sm text-right outline-none"
                      />
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground w-8">
                    {meta.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Run button */}
      <button
        type="button"
        onClick={run}
        disabled={running || selected_ids.size === 0}
        className="self-start inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {running ? (
          <>
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
            Lasketaan…
          </>
        ) : (
          "Aja skenaario"
        )}
      </button>

      {/* Result */}
      {scenario_result && (
        <ScenarioResultPanel
          breakdown={scenario_result}
          ceiling_eur={this_scenario_ceiling}
          full_ceiling_eur={ceiling_eur}
          period_days={period_days}
          zone_breakdown={result?.zone_breakdown ?? []}
          unallocated={result?.unallocated ?? null}
          zone_names={zone_names}
        />
      )}
    </div>
  );
}

// ── ScenarioResultPanel ───────────────────────────────────────────────────────

function ScenarioResultPanel({
  breakdown,
  ceiling_eur,
  full_ceiling_eur,
  period_days,
  zone_breakdown,
  unallocated,
  zone_names,
}: {
  breakdown: OptimizationPotentialBreakdown;
  ceiling_eur: number | null;
  full_ceiling_eur: number | null;
  period_days: number;
  zone_breakdown: ZoneSavingsAllocation[];
  unallocated: UnallocatedSavings | null;
  zone_names: Record<string, string>;
}) {
  const capture_pct =
    ceiling_eur && ceiling_eur > 0
      ? Math.round((breakdown.eur_month / ceiling_eur) * 100)
      : null;

  return (
    <div className={cn(cardBase, "p-5 flex flex-col gap-4")}>
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <MetricCard
          label="Säästö / kk"
          value={`€${breakdown.eur_month.toLocaleString("fi-FI", { maximumFractionDigits: 0 })}`}
          highlight="success"
        />
        <MetricCard
          label="Energiansäästö"
          value={`${breakdown.kwh_savings.toLocaleString("fi-FI", { maximumFractionDigits: 0 })} kWh`}
          sub={`${breakdown.savings_pct} %`}
          highlight="success"
        />
        {capture_pct !== null ? (
          <MetricCard
            label="Kattopotentiaalista"
            value={`${capture_pct} %`}
            sub={`kata €${(ceiling_eur ?? 0).toLocaleString("fi-FI", { maximumFractionDigits: 0 })}/kk`}
            highlight="primary"
          />
        ) : full_ceiling_eur !== null ? (
          <MetricCard
            label="Kaikki skenaariot"
            value={`€${full_ceiling_eur.toLocaleString("fi-FI", { maximumFractionDigits: 0 })}/kk`}
            highlight="primary"
          />
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap border-t border-border pt-3">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-[10px] text-muted-foreground mb-1">Tietopohja</p>
            <div className="flex items-center gap-2">
              <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-cyan-500/70 transition-all"
                  style={{
                    width: `${Math.round(breakdown.confidence * 100)}%`,
                  }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {Math.round(breakdown.confidence * 100)}%
              </span>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            ⓘ Arvio perustuu {period_days} vrk mittausdataan.
            {breakdown.confidence < 0.5 && " Alustava — puutteellinen data."}
          </p>
        </div>
      </div>

      {/* Zone breakdown (story #498) — an equal split of the scenario's building-wide
          savings, not a per-equipment measurement; see docs/spec/concepts.md § Flow 5. */}
      {unallocated && (
        <div className="border-t border-border pt-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Säästö vyöhykkeittäin
          </p>
          <div className="flex flex-col gap-1.5">
            {[...zone_breakdown]
              .sort((a, b) => b.eur_month - a.eur_month)
              .map((z) => (
                <div
                  key={z.zone_id}
                  className={cn(
                    cardSubtle,
                    "flex items-center justify-between gap-3 px-3 py-2",
                  )}
                >
                  <span className="text-sm truncate">
                    {zone_names[z.zone_id] ?? z.zone_id}
                  </span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {z.kwh_savings.toLocaleString("fi-FI", {
                        maximumFractionDigits: 0,
                      })}{" "}
                      kWh
                    </span>
                    <span className="text-sm font-semibold text-success-subtle-foreground tabular-nums">
                      €
                      {z.eur_month.toLocaleString("fi-FI", {
                        maximumFractionDigits: 0,
                      })}
                      /kk
                    </span>
                  </div>
                </div>
              ))}
            <div
              className={cn(
                cardSubtle,
                "flex items-center justify-between gap-3 px-3 py-2 border-dashed",
              )}
            >
              <span className="text-sm text-muted-foreground italic">
                Ei kohdistettu vyöhykkeelle
              </span>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-muted-foreground tabular-nums">
                  {unallocated.kwh_savings.toLocaleString("fi-FI", {
                    maximumFractionDigits: 0,
                  })}{" "}
                  kWh
                </span>
                <span className="text-sm font-semibold text-muted-foreground tabular-nums">
                  €
                  {unallocated.eur_month.toLocaleString("fi-FI", {
                    maximumFractionDigits: 0,
                  })}
                  /kk
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MetricCard ────────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  sub,
  muted,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  muted?: boolean;
  highlight?: "primary" | "success";
}) {
  return (
    <div className={cn(cardSurface, "bg-background/50 p-3")}>
      <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
      <p
        className={cn(
          "text-base font-semibold leading-tight",
          muted && "text-muted-foreground",
          highlight === "primary" && "text-primary",
          highlight === "success" && "text-success-subtle-foreground",
        )}
      >
        {value}
      </p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
